// src/utils/orderCalculations.js
import { formatCurrency } from './currencyUtil';

/**
 * Calculate line total for an order item
 * @param {number} quantity - Item quantity
 * @param {number} unitPrice - Unit price
 * @param {number} lineDiscount - Line discount amount
 * @param {number} taxRate - Tax rate (as decimal, e.g., 0.15 for 15%)
 * @returns {object} Line calculation details
 */
export const calculateLineTotal = (quantity, unitPrice, lineDiscount = 0, taxRate = 0) => {
  // Convert all inputs to numbers to handle string values
  const qty = parseFloat(quantity) || 0;
  const price = parseFloat(unitPrice) || 0;
  const discount = parseFloat(lineDiscount) || 0;
  const rate = parseFloat(taxRate) || 0;
  
  const subtotal = qty * price;
  const discountAmount = discount;
  const subtotalAfterDiscount = subtotal - discountAmount;
  const taxAmount = subtotalAfterDiscount * rate;
  const lineTotal = subtotalAfterDiscount + taxAmount;

  return {
    subtotal: Number(subtotal.toFixed(2)),
    discountAmount: Number(discountAmount.toFixed(2)),
    subtotalAfterDiscount: Number(subtotalAfterDiscount.toFixed(2)),
    taxAmount: Number(taxAmount.toFixed(2)),
    lineTotal: Number(lineTotal.toFixed(2))
  };
};

/**
 * Effective line discount for an offline sales item: the manual discount plus the value of free units.
 * @param {object} item - { lineDiscount, freeQuantity, unitPrice }
 * @returns {number}
 */
export const getEffectiveLineDiscount = (item) =>
  (parseFloat(item?.lineDiscount) || 0) + (parseInt(item?.freeQuantity, 10) || 0) * (parseFloat(item?.unitPrice) || 0);

/**
 * Calculate order totals
 * @param {Array} orderItems - Array of order items
 * @param {number} deliveryCharge - Delivery charge amount
 * @param {number} taxRate - Tax rate (as decimal)
 * @returns {object} Order total calculations
 */
export const calculateOrderTotals = (orderItems, deliveryCharge = 0, taxRate = 0) => {
  // Convert deliveryCharge to number to handle string values from forms/API
  const delivery = parseFloat(deliveryCharge) || 0;
  const rate = parseFloat(taxRate) || 0;
  
  let totalAmount = 0;
  let totalDiscount = 0;
  let totalTax = 0;
  let totalProductCost = 0;

  orderItems.forEach(item => {
    const lineCalc = calculateLineTotal(
      item.quantity,
      item.unitPrice,
      item.lineDiscount || 0,
      rate
    );
    
    totalAmount += lineCalc.subtotal;
    totalDiscount += lineCalc.discountAmount;
    totalTax += lineCalc.taxAmount;
    totalProductCost += lineCalc.subtotalAfterDiscount;
  });

  const grandTotal = totalProductCost + totalTax + delivery;

  return {
    totalAmount: Number(totalAmount.toFixed(2)),
    totalDiscount: Number(totalDiscount.toFixed(2)),
    totalProductCost: Number(totalProductCost.toFixed(2)),
    totalTax: Number(totalTax.toFixed(2)),
    deliveryCharge: Number(delivery.toFixed(2)),
    grandTotal: Number(grandTotal.toFixed(2))
  };
};

/**
 * Generate order number preview
 * @param {string} shopPrefix - Shop's order number prefix
 * @param {Date} date - Order date
 * @param {number} sequenceNumber - Sequence number (optional, defaults to 1)
 * @returns {string} Generated order number
 */
export const generateOrderNumberPreview = (shopPrefix, date = new Date(), sequenceNumber = 1) => {
  if (!shopPrefix) return '';
  
  const year = date.getFullYear().toString().slice(-2); // Last 2 digits of year
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const sequence = String(sequenceNumber).padStart(5, '0');
  
  return `${shopPrefix}${year}${month}${day}${sequence}`;
};

/**
 * Validate order item data
 * @param {object} item - Order item to validate
 * @returns {object} Validation result
 */
export const validateOrderItem = (item) => {
  const errors = {};
  
  if (!item.productId) {
    errors.productId = 'Product is required';
  }
  
  if (!item.quantity || item.quantity <= 0) {
    errors.quantity = 'Quantity must be greater than 0';
  }
  
  if (!item.unitPrice || item.unitPrice <= 0) {
    errors.unitPrice = 'Unit price must be greater than 0';
  }
  
  if (item.lineDiscount && item.lineDiscount < 0) {
    errors.lineDiscount = 'Line discount cannot be negative';
  }
  
  const lineTotal = calculateLineTotal(
    item.quantity || 0,
    item.unitPrice || 0,
    item.lineDiscount || 0
  );
  
  if (item.lineDiscount && item.lineDiscount > lineTotal.subtotal) {
    errors.lineDiscount = 'Discount cannot exceed line subtotal';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Calculate payment balance
 * @param {number} grandTotal - Order grand total
 * @param {Array} paymentRecords - Array of payment records
 * @returns {object} Payment balance information
 */
export const calculatePaymentBalance = (grandTotal, paymentRecords = []) => {
  const totalPaid = paymentRecords.reduce((sum, payment) => {
    // Convert paymentAmount to number to handle string inputs from form fields
    const paymentAmount = parseFloat(payment.paymentAmount) || 0;
    return sum + paymentAmount;
  }, 0);
  
  // Ensure grandTotal is also a number
  const orderTotal = parseFloat(grandTotal) || 0;
  
  const balance = orderTotal - totalPaid;
  const isFullyPaid = balance <= 0;
  const isOverpaid = balance < 0;
  
  return {
    totalPaid: Number(totalPaid.toFixed(2)),
    balance: Number(Math.abs(balance).toFixed(2)),
    isFullyPaid,
    isOverpaid,
    paymentStatus: isFullyPaid ? (isOverpaid ? 'overpaid' : 'paid') : 'pending'
  };
};

/**
 * Format currency amount
 * @param {number} amount - Amount to format
 * @param {string} currency - Currency code (default: null)
 * @returns {string} Formatted currency string
 * Use the same function in currencyUtil
 */
export { formatCurrency };

// export const formatCurrency = (amount, currency = 'LKR') => {
//   const numAmount = parseFloat(amount);
//   if (typeof numAmount !== 'number' || isNaN(numAmount)) return '0.00';
  
//   return new Intl.NumberFormat('en-US', {
//     style: 'decimal',
//     // currency: currency,
//     minimumFractionDigits: 2,
//     maximumFractionDigits: 2
//   }).format(numAmount);
// };

/**
 * @param {object} item - Order item (quantity, originalQuantity, baselineQuantity, crossSellById)
 * @param {object} ctx - { currentUserId, createdByUserId }
 * @returns {number} Max cross-sell quantity currently eligible
 */
export const getCrossSellMaxQty = (item, { currentUserId, createdByUserId }) => {
  const isOwner = item.crossSellById != null && item.crossSellById === currentUserId;
  const isCrossSoldByOther = item.crossSellById != null && item.crossSellById !== currentUserId;
  const isCreator = createdByUserId != null && createdByUserId === currentUserId;

  if (isCrossSoldByOther) return 0;
  if (isCreator) return item.quantity;

  const baseline = isOwner
    ? (Number(item.baselineQuantity) || 0)
    : (item.originalQuantity != null ? item.originalQuantity : 0);
  return Math.max(0, item.quantity - baseline);
};

/**
 * Check if order can be modified based on status
 * @param {string} statusKey - Order status key
 * @returns {boolean} Whether order can be modified
 */
export const canModifyOrder = (statusKey) => {
  const nonModifiableStatuses = ['settled', 'returned', 'cancelled'];
  return !nonModifiableStatuses.includes(statusKey);
};

/**
 * Get default payment type based on order total and settings
 * @param {number} grandTotal - Order grand total
 * @param {object} shopSettings - Shop settings
 * @returns {string} Default payment type key
 */
export const getDefaultPaymentType = (grandTotal, shopSettings = {}) => {
  // Default logic - can be customized based on business rules
  const total = parseFloat(grandTotal) || 0;
  const threshold = parseFloat(shopSettings.codThreshold) || 10000;
  
  if (total > threshold) {
    return 'fully_paid'; // Require full payment for high-value orders
  }
  return 'cash_on_delivery';
};